import { z } from 'zod'
import { actorSettingsSchema } from './settings'
import { CATALOGUE_QUERY_MAX, catalogueEntryCodec, catalogueEntryOf } from './catalogue'
import type { Expense } from '#model/entities/expense'
import { newExpenseSchema } from '#model/entities/expense'
import type { Item } from '#model/entities/item'
import { newPlaceSchema, placeSchema } from '#model/entities/place'
import type { Place } from '#model/entities/place'
import {
  convertedMinor,
  effectiveRate,
  isTripRateStale,
  rateChoiceSchema,
  receiptGap,
  tripMoney,
  tripTotal,
} from '#model/entities/trip'
import type { Trip } from '#model/entities/trip'
import { INT8_MAX } from '#model/support/decimal'
import { ERROR } from '#model/support/errors'
import { currencySchema, moneyCodec } from '#model/values/money'
import type { Money } from '#model/values/money'
import { isCalendarDay, latestDay, rateCodec, rateProviderSchema } from '#model/values/rates'
import type { ExchangeRate } from '#model/values/rates'
import { quantityCodec, unitPrice, unitPriceCodec } from '#model/values/units'

/**
 * An identifier the device names a row with, in lower case only. Postgres compares uuids without
 * case and answers in lower case, so an `AB12…` sent in would come back as `ab12…` and the device
 * would not find its own row in the reply (MOL-21, adversarial round 2, В). Refused rather than
 * folded: folding would still leave the device holding the spelling it sent.
 */
export const deviceIdSchema = z.uuid().regex(/^[0-9a-f-]+$/)

/**
 * A day a phone named at a tap (MOL-121): taken as it comes, and judged by the use case —
 * `isDeviceDay` — which drops what it cannot believe rather than refuse it. A clock at 1970, in year
 * 1 or past 9999 writes a day no calendar check passes, and a refused start or finish is set aside by
 * the queue for good: the one outcome Р-33 exists to prevent (adversarial rounds 2 П and 3 С). Only
 * the length is held, so the field carries no essay.
 */
export const deviceDaySchema = z.string().max(32)

/**
 * The body of «Начать поход».
 *
 * The identifier comes from the device, not the server (MOL-21, В-2): a trip started with no
 * signal has to be named by the expenses queued inside it, and a double tap has to meet its own
 * trip rather than a «another trip is open» about itself. That differs from the actor on
 * purpose — there the identifier is the proof of identity (MOL-8); here it only names a row that
 * the owner in the header already guards.
 *
 * The place is named, not picked by id: no screen lists places to pick from beyond the last
 * few, and the country and city come from the person's settings. Stores only until 0.3, for
 * the reason «Предложить товар» takes products only — a venue would enter the gate as a store.
 */
export const startTripBodySchema = z.strictObject({
  id: deviceIdSchema,
  // Optional only for a repeat from the old queue; a new trip must supply it.
  context: actorSettingsSchema.optional(),
  // The phone's today at the tap (MOL-121): a start may reach the server a day later from the queue,
  // and an account dates the trip by the day it began for the person. Absent from an old queue.
  startedOn: deviceDaySchema.optional(),
  place: z.strictObject({
    kind: z.literal('store'),
    name: newPlaceSchema.shape.name,
  }),
})
export type StartTripBody = z.infer<typeof startTripBodySchema>

/**
 * The body of «Добавить в поход». Exactly one field is required beyond the identifier — the
 * item; the rest may come later through the patch.
 *
 * `query` is what was typed before the item was picked. It is remembered in the same
 * transaction as the expense (MOL-11), so a pick exists only when the purchase does. Bounded
 * as the search bounds it: nothing longer could have been the query that found the item.
 *
 * `missedQuery` is the query that found nothing on the same screen before this one found the
 * item (MOL-45): the person's own word for it, learnt so that next time it finds the item too.
 * Optional both ways — a queue written before it existed is read as it was.
 */
export const addExpenseBodySchema = newExpenseSchema.omit({ tripId: true }).extend({
  id: deviceIdSchema,
  query: z.string().max(CATALOGUE_QUERY_MAX).optional(),
  missedQuery: z.string().max(CATALOGUE_QUERY_MAX).optional(),
})
export type AddExpenseBody = z.infer<typeof addExpenseBodySchema>

/** A moment on the wire: an ISO string there, a `Date` in the domain. */
export const isoDate = z.codec(z.iso.datetime(), z.date(), {
  decode: (wire) => new Date(wire),
  encode: (date) => date.toISOString(),
})

/** What the trip screen shows of a place: the name in «Ереван Сити · сегодня», and no more. */
const tripPlaceSchema = placeSchema.pick({ id: true, kind: true, name: true })
export type TripPlace = z.infer<typeof tripPlaceSchema>

const tripExpenseCodec = z.strictObject({
  id: z.uuid(),
  createdAt: isoDate,
  /** The catalogue card, not the item: `createdBy` is somebody's identity (MOL-12). */
  item: catalogueEntryCodec,
  quantity: quantityCodec.nullable(),
  amount: moneyCodec.nullable(),
  /** Computed, never entered and never stored — null until both the amount and the quantity are there. */
  unitPrice: unitPriceCodec.nullable(),
  /**
   * A purchase recorded from a receipt (MOL-126): its line as printed, shown under it, and the line's
   * discount — the amount is what was paid, the discount is beside it (owner, 30.09.2026).
   */
  printed: z.string().nullable().default(null),
  discount: moneyCodec.nullable().default(null),
})
export type TripExpenseView = z.output<typeof tripExpenseCodec>

/** What the prices say beside a receipt's sum (MOL-78, `receiptGap`): what the screen names under it. */
const receiptGapCodec = z.strictObject({
  kind: z.enum(['unpriced', 'over', 'under']),
  amount: moneyCodec,
})

/**
 * A trip as the screen shows it, and the answer of every route that changes one: the list, the
 * price per unit of each row and the total are all the server's, so the phone adds nothing up
 * and divides nothing (CLAUDE.md, «No business logic on the frontend»).
 *
 * Strict, as the catalogue card is: a reply that grew a field fails to parse on the client
 * rather than carrying it past a screen that never reads it — which is how a leak would go
 * unnoticed.
 */
export const tripViewCodec = z.strictObject({
  id: z.uuid(),
  /** The receipt the trip was recorded from (MOL-126), `null` for one typed by hand. */
  receiptId: z.uuid().nullable().default(null),
  startedAt: isoDate,
  finishedAt: isoDate.nullable(),
  finishedOnDeviceAt: isoDate.nullable().optional(),
  currency: currencySchema,
  /**
   * The rate the trip counts by: the snapshot, or — when it jumped and the person chose — the rate
   * before the jump, or their own for this trip (`source: 'personal'`). What `converted` uses.
   */
  rate: rateCodec.nullable(),
  /**
   * Who published the rate the trip **snapshotted** (MOL-22) — not necessarily the rate above:
   * after a jump the person may count by their own, and `rate.source` is then `personal` while
   * this still names the bank the trip took its snapshot from. Null when there is no snapshot
   * at all, and when the snapshot is the person's own rate from their exchanges (MOL-40).
   *
   * `source: 'fallback'` alone cannot be shown: the screen has to name the bank or the aggregator
   * it counts by, and the aggregator's terms require the name.
   */
  rateProvider: rateProviderSchema.nullable(),
  /**
   * When the snapshotted rate jumped (MOL-39, Р-19, Р-21): the jumped rate, the one before it if
   * there is one, the person's own if they entered it, and their choice — null until made. The
   * screen warns and offers «по новому / по прежнему / свой». Null when nothing jumped.
   */
  rateJump: z
    .strictObject({
      jumped: rateCodec,
      previous: rateCodec.nullable(),
      manual: rateCodec.nullable(),
      choice: rateChoiceSchema.nullable(),
    })
    .nullable(),
  /**
   * The official rate was over a week old when the trip started (Р-18): the screen says the rate
   * is as of its date and the bank has published nothing since.
   */
  rateStale: z.boolean(),
  place: tripPlaceSchema.strict(),
  /**
   * The place's city (MOL-120, adversarial Б1): «Ереван Сити» of Gyumri open while a start names
   * the one of Yerevan is another shop, and the choice of MOL-25 must say so. Beside `place`, never
   * inside: the phone keeps a trip in two caches the version before reads — it drops a field it
   * does not know at this level and reads `place` strictly. Optional for the server before it.
   */
  placeCity: placeSchema.shape.city.optional(),
  expenses: z.array(tripExpenseCodec),
  /**
   * What the trip came to (`tripMoney`, MOL-78): the receipt's sum when there is one, else one sum
   * of prices per currency — empty rather than zero when nothing is priced yet.
   */
  total: z.array(moneyCodec),
  /**
   * The receipt's sum, typed whole (MOL-78); the prices beside it, one per currency; and what the
   * two say about each other, null when they agree or cannot be set side by side. Defaults read an
   * older server, whose `total` is the prices.
   */
  receipt: moneyCodec.nullable().default(null),
  prices: z.array(moneyCodec).default([]),
  gap: receiptGapCodec.nullable().default(null),
  /**
   * The total in the trip's currency, converted by the rate the trip counts by. An estimate
   * for display, never a fact — null without a rate: an empty cache, or one currency (MOL-39).
   */
  converted: moneyCodec.nullable(),
  /** The account it was paid from and «списано» (MOL-115, Р-18); defaults read an older server. */
  accountId: z.uuid().nullable().default(null),
  debited: moneyCodec.nullable().default(null),
})
export type TripView = z.output<typeof tripViewCodec>

/** «No trip» is an ordinary state of the screen («Новый поход»), not a 404. */
export const currentTripResponseSchema = z.strictObject({
  trip: tripViewCodec.nullable(),
})
export type CurrentTripResponse = z.infer<typeof currentTripResponseSchema>

export const recentPlacesResponseSchema = z.strictObject({
  places: z.array(tripPlaceSchema.strict()),
})
export type RecentPlacesResponse = z.infer<typeof recentPlacesResponseSchema>

/**
 * «Брали здесь в прошлый раз» (MOL-209): the items of the person's last finished trip at the
 * trip's place before it, each once, in the order they were first bought there, but none the trip
 * asked about holds already (MOL-209, В-5 «а») — so they are not the whole of it. `previous` names
 * that trip and its moment in «Записаны», so the phone can tell it from the trips it remembers
 * by itself (В-1); `null` — no such trip, and then no items.
 */
export const previousItemsResponseSchema = z.strictObject({
  previous: z.strictObject({ tripId: z.uuid(), at: isoDate }).nullable(),
  items: z.array(catalogueEntryCodec),
  /**
   * What the trip asked about holds on the server, each once, in the order first bought (adversarial
   * В1 of review 3): the phone keeps that trip by it — its own copy may miss what another device added
   * — and so tells what `items` left out from what was removed since.
   */
  holds: z.array(catalogueEntryCodec),
  /**
   * The person's trip going on, as the server knows it, or `null` (adversarial Е1 of review 6): the
   * phone keeps it the newest of a place, and its own copy may still hold one removed or finished on
   * another device.
   */
  open: z.uuid().nullable(),
})
export type PreviousItemsResponse = z.output<typeof previousItemsResponseSchema>

/**
 * The total converted for display, or nothing. An estimate, never a fact — so one that does not
 * fit is shown as none rather than refusing the purchase the trip is being written with: the
 * answer is built inside the writer's transaction, and a throw here would undo the expense
 * (MOL-21, С-11).
 */
function estimate(total: Money, rate: ExchangeRate): Money | null {
  const minor = convertedMinor(total, rate)
  return minor > INT8_MAX ? null : { minor, currency: rate.base }
}

/** The one way a place becomes what the trip screen sees of it. */
export function tripPlaceOf(place: Place): TripPlace {
  return { id: place.id, kind: place.kind, name: place.name }
}

/** The receipt a trip was recorded from (MOL-126): its id, and each purchase's line as printed. */
export interface TripReceiptSource {
  readonly receiptId: string
  readonly lines: ReadonlyMap<string, { readonly printed: string; readonly discount: Money | null }>
}

/**
 * Assembles the trip the screen shows. Pure: the rows are read by the caller, and every number
 * here comes from a rule the domain already owns — `unitPrice`, `tripTotal`, `convertMoney`.
 *
 * An expense whose item is missing is a defect rather than a state: the foreign key holds it,
 * so reaching the throw means the caller read the rows from two different moments. A plain
 * Error on purpose — a DomainError would reach the client as a 404, «not found», and a broken
 * server would read as a missing row with no line in the log.
 */
export function tripViewOf(
  trip: Trip,
  place: Place,
  expenses: readonly Expense[],
  items: readonly Item[],
  source: TripReceiptSource | null = null,
): TripView {
  const byId = new Map(items.map((item) => [item.id, item]))

  const rows = expenses.map((expense): TripExpenseView => {
    const item = byId.get(expense.itemId)
    if (!item) throw new Error(`trip ${trip.id}: item ${expense.itemId} was not read`)
    return {
      id: expense.id,
      createdAt: expense.createdAt,
      item: catalogueEntryOf(item),
      quantity: expense.quantity,
      amount: expense.amount,
      unitPrice:
        expense.amount && expense.quantity ? unitPrice(expense.amount, expense.quantity) : null,
      printed: source?.lines.get(expense.id)?.printed ?? null,
      discount: source?.lines.get(expense.id)?.discount ?? null,
    }
  })

  const prices = tripTotal(expenses)
  const total = tripMoney(trip.receipt, expenses)
  const inTripCurrency = total.find((money) => money.currency === trip.currency)
  const rate = effectiveRate(trip)

  return {
    id: trip.id,
    receiptId: source?.receiptId ?? null,
    startedAt: trip.startedAt,
    finishedAt: trip.finishedAt,
    finishedOnDeviceAt: trip.finishedOnDeviceAt ?? null,
    currency: trip.currency,
    rate,
    rateProvider: trip.rateProvider,
    rateJump:
      trip.rate && trip.rateJumped
        ? {
            jumped: trip.rate,
            previous: trip.previousRate,
            manual: trip.manualRate,
            choice: trip.rateChoice,
          }
        : null,
    rateStale: isTripRateStale(trip),
    place: tripPlaceOf(place),
    placeCity: place.city,
    expenses: rows,
    total: [...total],
    receipt: trip.receipt,
    prices: [...prices],
    gap: trip.receipt ? receiptGap(trip.receipt, expenses) : null,
    converted: rate && inTripCurrency ? estimate(inTripCurrency, rate) : null,
    accountId: trip.accountId,
    debited: trip.debited,
  }
}

/**
 * «Сумма по чеку» (MOL-78): the receipt's sum whole, or `null` to take it off. Sent whole each time
 * through the trip's queue, so a repeat is the same write.
 */
export const tripReceiptBodySchema = z.strictObject({
  receipt: moneyCodec
    .refine((value) => value.minor > 0n, { error: ERROR.INVALID_AMOUNT })
    .nullable(),
})
export type TripReceiptBody = z.output<typeof tripReceiptBodySchema>

/**
 * «Считать по новому курсу / по прежнему / по своему» (MOL-39, Р-19, Р-21). Repeatable: the same
 * choice twice is one. The own rate travels as the decimal a person types — `parseRate` reads it.
 */
export const rateChoiceBodySchema = z.discriminatedUnion('choice', [
  z.strictObject({ choice: z.literal('jumped') }),
  z.strictObject({ choice: z.literal('previous') }),
  // `per`: the currency the number is «за 1» of, when that is not the snapshot's base — the sheet
  // asks on the side a person reads a rate by (MOL-81, adversarial А). Absent means the base.
  z.strictObject({
    choice: z.literal('manual'),
    rate: z.string().max(40),
    per: currencySchema.optional(),
  }),
])
export type RateChoiceBody = z.infer<typeof rateChoiceBodySchema>

/**
 * The earliest moment a phone may claim it closed a trip.
 *
 * It is the only key the history is ordered by, so a date no phone could have produced
 * rearranges the whole list for ever — and `0001-01-01`, which the driver hands back as
 * `2001-01-01`, would print one year on the card and sort by another two thousand apart (Б1).
 *
 * **Both ends are judged where the clock is — in the use case — and neither is a refusal**
 * (Р-33). The floor lived in the schema for a day, and that made the two ends behave in
 * opposite ways: a phone whose clock ran ahead had its time dropped and its trip closed, while
 * one whose clock had fallen back got a 400. The queue never retries a refusal, so that trip
 * could never be closed at all, and the local mark of its completion was dropped beside it —
 * and a battery that died is the ordinary way a clock falls back (В2, Г1).
 */
export const DEVICE_TIME_EPOCH = new Date('2000-01-01T00:00:00.000Z')

/** How far ahead of the server a phone's clock may be and still be believed. */
export const DEVICE_TIME_AHEAD_MS = 24 * 60 * 60 * 1000

/** Whether a moment a phone named is one it could plausibly have lived through. */
export function isDeviceTime(at: Date, now: Date): boolean {
  return at >= DEVICE_TIME_EPOCH && at.getTime() - now.getTime() <= DEVICE_TIME_AHEAD_MS
}

/**
 * Whether a day a phone named at a tap — `startedOn`, `finishedOn` (MOL-121) — is one it could have
 * lived through: a calendar day, from 2000 on as a device time is, and not past the latest day on
 * Earth. A day that fails this is dropped, not refused (Р-33).
 */
export function isDeviceDay(day: string, now: Date): boolean {
  return (
    isCalendarDay(day) &&
    day >= DEVICE_TIME_EPOCH.toISOString().slice(0, 10) &&
    day <= latestDay(now)
  )
}

/** Old queued finishes have no device time; retries preserve whichever time first arrived. */
export const finishTripBodySchema = z.strictObject({
  finishedOnDeviceAt: isoDate.optional(),
  // The phone's today at the tap (MOL-121): the day «Деньги» file the trip under, beside the
  // spendings of that day. Absent from an old queue — then the server's day of the moment above.
  finishedOn: deviceDaySchema.optional(),
})
export type FinishTripBody = z.output<typeof finishTripBodySchema>

/**
 * «Вернуть» (MOL-76). `finish` brings the trip back finished, in one step: a trip finished on the
 * phone with no signal and removed before its `finish` left is open on the server, and brought back
 * open after the next trip started it met that trip — a refusal about two open trips the person never
 * held, and the trip with its purchases gone ten minutes later (adversarial round 3, В1).
 */
export const restoreTripBodySchema = z.strictObject({
  finish: finishTripBodySchema.optional(),
})
export type RestoreTripBody = z.output<typeof restoreTripBodySchema>

export const TRIP_HISTORY_PAGE_SIZE = 20
export const tripHistoryCursorSchema = z.strictObject({
  // Keep PostgreSQL microseconds on the wire: decoding to Date would skip boundary rows.
  at: z.iso.datetime().refine((at) => !at.startsWith('0000-')),
  id: z.uuid(),
})
export type TripHistoryCursor = z.infer<typeof tripHistoryCursorSchema>
export const tripHistoryQuerySchema = z
  .strictObject({
    before: tripHistoryCursorSchema.shape.at.optional(),
    beforeId: z.uuid().optional(),
  })
  .refine((q) => (q.before === undefined) === (q.beforeId === undefined))

export const tripHistoryEntryCodec = z.strictObject({
  id: z.uuid(),
  place: tripPlaceSchema.strict(),
  startedAt: isoDate,
  finishedAt: isoDate,
  finishedOnDeviceAt: isoDate.nullable(),
  /**
   * How many purchases and what they came to, one sum per currency (MOL-128, В-4): a row of
   * «Записаны» says «12 позиций · 25 сент.» and «9 870 ֏». `null` from a server that predates
   * them — unknown, which is not zero — so the screen says nothing rather than «0 позиций».
   */
  itemCount: z.int().nonnegative().nullable().default(null),
  total: z.array(moneyCodec).nullable().default(null),
  /** Recorded from a receipt (MOL-126): «· из чека» beside the row. */
  fromReceipt: z.boolean().default(false),
})
export type TripHistoryEntry = z.output<typeof tripHistoryEntryCodec>
export const tripHistoryCodec = z.strictObject({
  trips: z.array(tripHistoryEntryCodec).max(TRIP_HISTORY_PAGE_SIZE),
  nextCursor: tripHistoryCursorSchema.nullable(),
})
export type TripHistory = z.output<typeof tripHistoryCodec>
